import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  message, Modal, Table, Button, Dropdown, Menu,
  Pagination, ConfigProvider, Tag, Tooltip, DatePicker,
  Input,
} from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { MakeApiCall } from "../../../apis";
import { DefaultAdminPage, DefaultUserPage, Types } from "../../../config";
import { fakeImage } from "../../../config/fakeData";
import UserModal from "./user-config";
import { Helmet } from "react-helmet";
import { debounce } from "lodash";
import { Wrapper } from "./style";
import { useThemeMode } from "../../../contexts/ThemeContext";
import { getLocalAntdTheme } from "../../../theme/antdTheme";

const findRoutes = {
  superAdmin: DefaultAdminPage,
  admin: DefaultAdminPage,
  manager: DefaultAdminPage,
  user: DefaultUserPage,
};

// ─── Inline editable Trial Expires At cell ────────────────────────────────────
const EditableTrialExpiry = ({ value, onSave, saving }) => {
  const [editing, setEditing]   = useState(false);
  const [draft,   setDraft]     = useState(null);

  const handleEdit = () => {
    setDraft(value ? dayjs.unix(parseInt(value, 10)) : null);
    setEditing(true);
  };

  const handleConfirm = async () => {
    if (!draft) return;
    await onSave(String(draft.unix()));
    setEditing(false);
  };

  const handleCancel = () => {
    setEditing(false);
    setDraft(null);
  };

  if (editing) {
    return (
        <div className="d-flex align-items-center gap-2 manage-user-trial-edit-wrap" onClick={e => e.stopPropagation()}>
          <DatePicker
              value={draft}
              onChange={setDraft}
              format="DD/MM/YYYY"
              size="small"
              style={{ flex: 1 }}
              allowClear={false}
              autoFocus
              disabledDate={current => current && current < dayjs().startOf("day")}
          />
          <Tooltip title="Save">
            <Button type="primary" size="small" icon={<CheckOutlined />} loading={saving} disabled={!draft} className="manage-user-trial-save-btn" onClick={handleConfirm} />
          </Tooltip>
          <Tooltip title="Cancel">
            <Button size="small" icon={<CloseOutlined />} disabled={saving} onClick={handleCancel} />
          </Tooltip>
        </div>
    );
  }

  const ts        = parseInt(value, 10);
  const formatted = ts ? new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(ts * 1000)) : null;
  const diffDays  = ts ? Math.ceil((ts * 1000 - Date.now()) / 86_400_000) : null;
  const isExpired = diffDays !== null && diffDays < 0;

  return (
      <div className="d-flex align-items-center justify-content-center gap-2 manage-user-trial-read-wrap">
        <span className={isExpired ? "manage-user-trial-expired" : ""}>{formatted ?? <span className="text-muted">—</span>}</span>
        <Tooltip title="Edit trial expiry date">
          <EditOutlined className="manage-user-trial-edit-icon" onClick={handleEdit} />
        </Tooltip>
      </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function UserList(props) {
  const { themeMode } = useThemeMode();
  const { setData, userData } = props;
  const navigate = useNavigate();

  const [totalPage, setTotalPage] = useState(0);
  const [pageSize,  setPageSize]  = useState(10);
  const [page,      setPage]      = useState(1);
  const [loading,   setLoading]   = useState(true);
  const [list,      setList]      = useState([]);
  const [searchText, setSearchText] = useState("");
  const [sortParam, setSortParam] = useState(""); // New sorting state

  const [savingRowId, setSavingRowId] = useState(null);

  // Helper to determine sortOrder for columns
  const getSortOrder = (columnKey) => {
    if (sortParam === columnKey) return 'ascend';
    if (sortParam === `-${columnKey}`) return 'descend';
    return null;
  };

  const getUserList = async (data) => {
    setLoading(true);
    let url = `users?search_text=${data?.searchText ?? searchText}&page=${data?.page || page || 1}&per-page=${data?.pageSize || pageSize}`;

    // Append sort if exists
    const currentSort = data?.sortParam !== undefined ? data.sortParam : sortParam;
    if (currentSort) {
      url += `&sort=${currentSort}`;
    }

    const response = await MakeApiCall(url, "get", null, true, {}, userData?.token);

    if (response?.status) {
      setTotalPage(response?.data?.pagination?.totalCount || 0);
      setPage(response?.data?.pagination?.page || 1);
      setList(response?.data?.records || []);
    } else {
      message.warning(response?.message);
      setList([]);
    }
    setLoading(false);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    let newSortValue = "";

    if (activeSorter.order) {
      const field = activeSorter.field || activeSorter.columnKey;
      newSortValue = activeSorter.order === "ascend" ? field : `-${field}`;
    }

    setSortParam(newSortValue);
    setPage(1);
    getUserList({ page: 1, sortParam: newSortValue });
  };

  const DeleteUser = async (id) => {
    message.destroy();
    message.loading("Loading...", 0);
    const response = await MakeApiCall(`user/${id}`, "delete", null, true, {}, userData?.token);
    if (response?.status) {
      getUserList();
      message.destroy();
      message.success(response?.message);
    } else {
      message.destroy();
      message.warning(response?.message);
    }
  };

  const UpdateTrialExpiry = async (userId, unixTimestampStr) => {
    setSavingRowId(userId);
    message.destroy();
    try {
      const response = await MakeApiCall(`user/${userId}`, "put", { trial_expires_at: unixTimestampStr }, true, {}, userData?.token);
      if (response?.status === true) {
        setList(prev => prev.map(u => u.id === userId ? { ...u, trial_expires_at: parseInt(unixTimestampStr, 10) } : u));
        message.success("Trial expiry updated successfully");
      } else {
        message.warning(response?.message || "Failed to update trial expiry");
      }
    } catch {
      message.error("Something went wrong");
    } finally {
      setSavingRowId(null);
    }
  };

  useEffect(() => {
    getUserList();
  }, [page, pageSize, searchText]);

  const SwitchAccount = async (id) => {
    const response = await MakeApiCall(`switch-user`, "post", { id }, true, {}, userData?.token);
    if (response?.status === true) {
      const type = Types?.[response?.data?.user_data?.user_type];
      setData({ admin: userData, user: response?.data, loginType: type, token: response?.data?.auth_token_data?.access_token });
      navigate(findRoutes?.[type]);
    } else {
      message.warning(response?.message);
    }
  };

  const [visible,      setVisible]      = useState(false);
  const [editingUser,  setEditingUser]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleCancel = () => { setVisible(false); setEditingUser(null); };

  const CreateUser = async (data) => {
    message.destroy(); message.loading("Loading...", 0);
    const response = await MakeApiCall(`sign-up`, "post", { ...data, user_type: 2 }, true, {}, userData?.token);
    if (response?.status === true) { handleCancel(); getUserList(); message.destroy(); message.success(response?.message || "Successfully created"); }
    else { message.destroy(); message.warning(response?.message || "Failed to create"); }
  };

  const UpdateUser = async (data) => {
    if (!editingUser?.id) return;
    message.destroy(); message.loading("Loading...", 0);
    const response = await MakeApiCall(`user/${editingUser?.id}`, "put", data, true, {}, userData?.token);
    if (response?.status === true) { handleCancel(); getUserList(); message.destroy(); message.success(response?.message || "Successfully updated"); }
    else { message.destroy(); message.warning(response?.message || "Failed to update"); }
  };

  const handleSubmit = (values) => { if (editingUser) { UpdateUser(values); } else { CreateUser(values); } setVisible(false); setEditingUser(null); };
  const handleEdit   = (user) => { setEditingUser(user); setVisible(true); };
  
  const handleSearch1 = (e)    => { setSearchText(e.target.value); setPage(1); };

  const onSearch = debounce((value) => {
    setSearchText(value); 
    setPage(1);
  }, 400);

  const isRestricted = userData?.user?.user_data?.user_type === 3 || userData?.user?.user_data?.user_type === 4;

  const formatTimestamp = (val) => {
    const ts = parseInt(val, 10);
    if (!ts) return null;
    return new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }).format(new Date(ts * 1000));
  };

  const getRemainingDays = (trialExpiresAt) => {
    const ts = parseInt(trialExpiresAt, 10);
    if (!ts) return null;
    return Math.ceil((ts * 1000 - Date.now()) / (1000 * 60 * 60 * 24));
  };

  const columns1 = [
    { title: "#", key: "index", render: (_, __, index) => (page - 1) * pageSize + index + 1 },
    {
      title: "User Details", dataIndex: "name", key: "name", sorter: true, sortOrder: getSortOrder('name'),
      render: (_, record) => (
          <div className="d-flex align-items-center">
            <div className="symbol symbol-circle symbol-50px overflow-hidden me-3"><img src={record?.photo || fakeImage} alt="" /></div>
            <div className="d-flex flex-column"><Link href className="manage-user-name-link text-hover-danger mb-1 fw-bolder">{record.name || ""}</Link><span>{record.email || ""}</span></div>
          </div>
      ),
    },
    { title: "Seller Name", dataIndex: "seller_name", key: "seller_name", sorter: true, sortOrder: getSortOrder('seller_name'), render: (text) => <span>{text || "-"}</span> },
    { title: "Contact No.", dataIndex: "contact_no", key: "contact_no", render: (text) => <span>{text || "N/A"}</span> },
    {
      title: "SP API", dataIndex: "is_amz_sp_api_connected", key: "is_amz_sp_api_connected", align: "center", sorter: true, sortOrder: getSortOrder('is_amz_sp_api_connected'),
      render: (val) => val === 1 ? <Tag color="success">Connected</Tag> : <Tag color="error">Not Connected</Tag>,
    },
    {
      title: "Ads API", dataIndex: "is_amz_ads_connected", key: "is_amz_ads_connected", align: "center", sorter: true, sortOrder: getSortOrder('is_amz_ads_connected'),
      render: (val) => val === 1 ? <Tag color="success">Connected</Tag> : <Tag color="error">Not Connected</Tag>,
    },
    {
      title: "Onboarded At", dataIndex: "is_data_sync_email_sent", key: "is_data_sync_email_sent", align: "center", sorter: true, sortOrder: getSortOrder('is_data_sync_email_sent'),
      render: (val) => {
        const formatted = formatTimestamp(val);
        return formatted ? <Tooltip title="First data sync completed"><span>{formatted}</span></Tooltip> : <Tag color="warning">In Progress</Tag>;
      },
    },
    {
      title: "Trial Expires At", dataIndex: "trial_expires_at", key: "trial_expires_at", align: "center", sorter: true, sortOrder: getSortOrder('trial_expires_at'),
      render: (val, record) => {
        if (isRestricted) {
          const ts = parseInt(val, 10);
          const formatted = ts ? new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(ts * 1000)) : null;
          return <span>{formatted ?? <span className="text-muted">—</span>}</span>;
        }
        return <EditableTrialExpiry value={val} saving={savingRowId === record.id} onSave={(unixStr) => UpdateTrialExpiry(record.id, unixStr)} />;
      },
    },
    {
      title: "Last Login At", dataIndex: "last_login_at", key: "last_login_at", align: "center", sorter: true, sortOrder: getSortOrder('last_login_at'),
      render: (val) => {
        const formatted = formatTimestamp(val);
        return <span>{formatted || '-'}</span>;
      },
    },
    {
      title: "Remaining Days", dataIndex: "trial_expires_at", key: "remaining_days", align: "center", sorter: true, sortOrder: getSortOrder('remaining_days'),
      render: (val) => {
        const days = getRemainingDays(val);
        if (days === null) return <span className="text-muted">—</span>;
        if (days < 0) return <Tooltip title={`Expired ${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} ago`}><Tag color="error">Expired</Tag></Tooltip>;
        if (days === 0) return <Tooltip title="Trial expires today"><Tag color="warning">Today</Tag></Tooltip>;
        const color = days <= 7 ? "orange" : days <= 15 ? "gold" : "green";
        return <Tag color={color} style={{ minWidth: 60, textAlign: "center" }}>{days} day{days !== 1 ? "s" : ""}</Tag>;
      },
    },
    {
      title: "Switch User", key: "switchUser",
      render: (_, row) => <Link onClick={() => SwitchAccount(row?.id)} href="user-dashboard.html" className="d-flex align-items-center btn btn-danger justify-content-center py-2 px-0 fs-7 fw-bolder w-150px">Switch User <i className="ki-outline ki-arrow-mix fs-1 ms-3" /></Link>,
    },
    { title: "Created at", dataIndex: "created_at", key: "created_at", render: (timestamp) => new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }).format(new Date(timestamp * 1000)) },
    { title: "Updated at", dataIndex: "updated_at", key: "updated_at", render: (timestamp) => new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true }).format(new Date(timestamp * 1000)) },
    {
      title: "Action", key: "action",
      render: (_, row) => (
          <Dropdown overlay={menu(row, isRestricted)} trigger={["click"]} disabled={isRestricted}>
            <Button type="link" disabled={isRestricted}>
              <i className={`ki-solid ki-dots-vertical fs-1 ${isRestricted ? "manage-user-action-icon--disabled" : "manage-user-action-icon"}`} />
            </Button>
          </Dropdown>
      ),
    },
  ].filter(col => !(isRestricted && col.key === "action"));

  const menu = (row, isRestricted = false) => (
      <Menu className="min-w-200px p-3">
        <Menu.Item key="1" className="d-flex align-items-center mb-2" disabled={isRestricted} onClick={() => !isRestricted && handleEdit(row)}>
          <i className="ki-outline ki-pencil fs-3 me-2" />Edit user
        </Menu.Item>
        <Menu.Item key="2" className="d-flex align-items-center" disabled={isRestricted} onClick={() => !isRestricted && setDeleteTarget(row)}>
          <i className="ki-outline ki-trash fs-3 me-2 text-danger" /><span className="text-danger">Delete user</span>
        </Menu.Item>
      </Menu>
  );

  const theme = getLocalAntdTheme(themeMode, "#f45b68");

  return (
      <Wrapper>
        <Helmet><title>Manage User</title></Helmet>

        <div className="mb-10 mx-n9 px-10 py-5 filter manage-user-search-wrap">
          <div className="position-relative">
            {/* <i className="ki-outline ki-magnifier text-gray-800 fs-2 position-absolute top-50 translate-middle-y ms-4" /> */}
            {/* <input type="text" value={searchText} onChange={handleSearch} className="form-control form-control-solid text-dark w-300px border border-gray-300 fs-7 ps-12 all-search" placeholder="Search by name or email..." /> */}
            <Input
              variant="filled"
              size="large"
              placeholder="Search by name or email..."
              prefix={<i className="ki-outline ki-magnifier manage-user-search-icon fs-2 position-absolute top-50 translate-middle-y ms-n8" />}
              onChange={(e) => onSearch(e.target.value)}
              style={{ width: 300 }}
              className="fs-7 ps-12 h-40px manage-user-search-input"
            />
          </div>
        </div>

        <div className="row gy-5 g-xl-5">
          <div className="col-xxl-12">
            <div className="card card-xxl-stretch mb-5 mb-xl-8">
              <div className="card-header border-bottom-dashed">
                <h3 className="card-title fw-bolder">Users List</h3>
              </div>
              <div className="card-body py-2">
                <div className="table-responsive">
                  <Table
                      scroll={{ x: "max-content" }}
                      columns={columns1}
                      loading={loading}
                      dataSource={list}
                      rowKey="id"
                      pagination={false}
                      onChange={handleTableChange} // Added sorting handler
                      components={{
                        table: (props) => <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...props} />,
                        header: {
                          wrapper: (props) => <thead {...props} />,
                          row:     (props) => <tr {...props} className="fw-bolder manage-user-table-header-row bg-cus" />,
                          cell:    (props) => <th {...props} />,
                        },
                      }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row gy-5 g-xl-5">
          <div className="col-xxl-12">
            <div className="card card-xxl-stretch mb-5 mb-xl-8">
              <div className="card-body py-2">
                <ConfigProvider theme={theme}>
                  <Pagination
                      className="mt-3"
                      current={page}
                      pageSize={pageSize}
                      total={totalPage}
                      showSizeChanger
                      onChange={(currentPage, newPageSize) => {
                        setPage(currentPage);
                        setPageSize(newPageSize);
                      }}
                      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                  />
                </ConfigProvider>
              </div>
            </div>
          </div>
        </div>

        <UserModal visible={visible} onCancel={handleCancel} onSubmit={handleSubmit} initialValues={editingUser} />

        <Modal open={!!deleteTarget} onCancel={() => setDeleteTarget(null)} footer={null} centered width={420} className="manage-user-delete-modal">
          <div className="text-center px-2 py-4">
            <div className="mb-4" style={{ fontSize: 48, lineHeight: 1 }}>🗑️</div>
            <h4 className="fw-bolder manage-user-delete-title mb-2">Delete User?</h4>
            <p className="text-muted mb-6">You are about to delete <span className="fw-bold manage-user-delete-name">{deleteTarget?.name}</span>.</p>
            <div className="d-flex justify-content-center gap-3">
              <Button size="large" onClick={() => setDeleteTarget(null)} style={{ minWidth: 110 }}>Cancel</Button>
              <Button danger type="primary" size="large" style={{ minWidth: 110 }} onClick={() => { DeleteUser(deleteTarget?.id); setDeleteTarget(null); }}>Yes, Delete</Button>
            </div>
          </div>
        </Modal>
      </Wrapper>
  );
}